import { useState } from "react";
import { useGrowth, useRetention } from "../lib/api";
import { fmtNumber, fmtPercent } from "../lib/format";
import { AppHeader, useAppId } from "../components/app-header";
import { StatsToolbar } from "../components/layout";
import { StatTile } from "../components/stats";
import { RetentionGrid } from "../components/charts/small";
import { Card, CardHeader, Empty, Segmented, Skeleton, StatGrid, ErrorState } from "../components/ui";

export function Retention() {
  const appId = useAppId();
  const [period, setPeriod] = useState<"day" | "week">("week");
  const r = useRetention(appId, period);
  const g = useGrowth(appId);

  return (
    <>
      <AppHeader title="Retention" />
      <StatsToolbar appId={appId}>
        <Segmented aria-label="Cohort period" items={[{ value: "day", label: "Daily" }, { value: "week", label: "Weekly" }]} value={period} onChange={(v) => setPeriod(v as "day" | "week")} />
      </StatsToolbar>
      <div className="space-y-4">
        <Card>
          {g.isError ? (
            <ErrorState error={g.error} />
          ) : (
            <StatGrid loading={!g.data}>
              {g.data && (
                <>
                  <StatTile label="DAU" value={fmtNumber(g.data.dau)} hint="Unique visitors in the last 24h of the range" />
                  <StatTile label="WAU" value={fmtNumber(g.data.wau)} />
                  <StatTile label="MAU" value={fmtNumber(g.data.mau)} />
                  <StatTile label="Stickiness" value={fmtPercent(g.data.stickiness)} hint="DAU / MAU" />
                  <StatTile label="New visitors" value={fmtNumber(g.data.newVisitors)} hint="New: first seen in this period and haven't come back yet." />
                  <StatTile label="Returning visitors" value={fmtNumber(g.data.returningVisitors)} hint="Returning: visited before this period, or came back after 30+ minutes away." />
                </>
              )}
            </StatGrid>
          )}
        </Card>
        <Card>
          <CardHeader
            title={`${period === "day" ? "Daily" : "Weekly"} cohort retention`}
            subtitle={`Visitors grouped by the ${period} they were first seen; each cell is the share active again ${period === "day" ? "N days" : "N weeks"} later.`}
          />
          <div className="px-2 pb-4">
            {r.isError ? (
              <ErrorState error={r.error} />
            ) : r.isPending ? (
              <Skeleton className="mx-2 h-64" />
            ) : r.data?.cohorts.length ? (
              <RetentionGrid cohorts={r.data.cohorts} period={period} />
            ) : (
              <Empty title="No new visitors in this range">Cohorts are built from visitors first seen inside the selected range.</Empty>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
